import { useState, useEffect, useRef, useCallback } from "react";
import { MessageSquare, Send, ArrowLeft, Plus, Search, User, Check, CheckCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import Navigation from "../components/Navigation";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ChatMessages({ user, onLogout }) {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations`, { headers });
      setConversations(res.data);
    } catch (err) {
      console.error("Failed to load conversations:", err);
    }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API}/chat/conversations/${convId}/messages`, { headers });
      setMessages(res.data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      console.error("Failed to load messages:", err);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Poll for new messages when in a conversation
  useEffect(() => {
    if (activeConv) {
      fetchMessages(activeConv.id);
      pollRef.current = setInterval(() => {
        fetchMessages(activeConv.id);
        fetchConversations();
      }, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeConv, fetchMessages, fetchConversations]);

  const handleSend = async () => {
    if (!newMessage.trim() || !activeConv) return;
    setSending(true);
    try {
      const otherId = activeConv.other_user?.id;
      await axios.post(`${API}/chat/send`, {
        recipient_id: otherId,
        content: newMessage.trim(),
        conversation_id: activeConv.id,
      }, { headers });
      setNewMessage("");
      fetchMessages(activeConv.id);
      fetchConversations();
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const startNewChat = async (recipientId) => {
    try {
      const res = await axios.post(`${API}/chat/send`, {
        recipient_id: recipientId,
        content: "Hi! 👋",
      }, { headers });
      setShowNewChat(false);
      await fetchConversations();
      // Open the new conversation
      const convId = res.data.conversation_id;
      const convs = (await axios.get(`${API}/chat/conversations`, { headers })).data;
      const newConv = convs.find(c => c.id === convId);
      if (newConv) setActiveConv(newConv);
    } catch (err) {
      toast.error("Failed to start conversation");
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const res = await axios.get(`${API}/chat/users`, { headers });
      setAvailableUsers(res.data);
      setShowNewChat(true);
    } catch (err) {
      toast.error("Failed to load users");
    }
  };

  const filteredUsers = availableUsers.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const roleLabel = (role) => {
    const map = { coach: "Coach", "team-admin": "Admin", "system-admin": "Admin", "sport-performance-consultant": "Consultant", "school-admin": "Admin", athlete: "Athlete" };
    return map[role] || role;
  };

  const roleBadgeColor = (role) => {
    if (["coach", "team-admin", "system-admin", "sport-performance-consultant", "school-admin"].includes(role)) return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
    return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300";
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden" style={{ height: "calc(100vh - 140px)" }}>
          <div className="flex h-full">
            {/* Conversation List */}
            <div className={`w-full sm:w-80 border-r border-gray-200 dark:border-gray-800 flex flex-col ${activeConv ? "hidden sm:flex" : "flex"}`}>
              <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Messages</h2>
                  <Button data-testid="new-chat-btn" onClick={loadAvailableUsers} size="sm" variant="outline" className="h-8 w-8 p-0">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {showNewChat ? (
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <button onClick={() => setShowNewChat(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                        <ArrowLeft className="w-4 h-4 text-gray-500" />
                      </button>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">New Conversation</span>
                    </div>
                    <div className="relative mb-3">
                      <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                      <input
                        data-testid="search-users-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search users..."
                        className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      />
                    </div>
                    <div className="space-y-1">
                      {filteredUsers.map((u) => (
                        <button
                          key={u.id}
                          data-testid={`user-${u.id}`}
                          onClick={() => startNewChat(u.id)}
                          className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                        >
                          <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{u.name}</p>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${roleBadgeColor(u.role)}`}>{roleLabel(u.role)}</span>
                          </div>
                        </button>
                      ))}
                      {filteredUsers.length === 0 && <p className="text-center text-sm text-gray-400 py-4">No users found</p>}
                    </div>
                  </div>
                ) : conversations.length > 0 ? (
                  conversations.map((conv) => (
                    <button
                      key={conv.id}
                      data-testid={`conversation-${conv.id}`}
                      onClick={() => setActiveConv(conv)}
                      className={`w-full flex items-center gap-3 p-3.5 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left ${activeConv?.id === conv.id ? "bg-emerald-50 dark:bg-emerald-900/20" : ""}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{conv.other_user?.name || "Unknown"}</p>
                          <span className="text-[10px] text-gray-400 flex-shrink-0">{formatTime(conv.last_message_at)}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{conv.last_message || "No messages yet"}</p>
                          {conv.unread_count > 0 && (
                            <span className="ml-2 w-5 h-5 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">{conv.unread_count}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-12 px-4">
                    <MessageSquare className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No conversations yet</p>
                    <Button onClick={loadAvailableUsers} size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" />Start a Conversation
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${!activeConv ? "hidden sm:flex" : "flex"}`}>
              {activeConv ? (
                <>
                  {/* Chat Header */}
                  <div className="p-3.5 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3">
                    <button onClick={() => setActiveConv(null)} className="sm:hidden p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                      <ArrowLeft className="w-5 h-5 text-gray-500" />
                    </button>
                    <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                      <User className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{activeConv.other_user?.name || "Unknown"}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${roleBadgeColor(activeConv.other_user?.role)}`}>
                        {roleLabel(activeConv.other_user?.role)}
                      </span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map((msg) => {
                      const isMe = msg.sender_id === user?.id;
                      return (
                        <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${isMe
                            ? "bg-emerald-600 text-white rounded-br-md"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-md"
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <div className={`flex items-center justify-end gap-1 mt-1 ${isMe ? "text-emerald-200" : "text-gray-400"}`}>
                              <span className="text-[10px]">{formatTime(msg.created_at)}</span>
                              {isMe && (msg.read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="p-3 border-t border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-2">
                      <input
                        data-testid="message-input"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2.5 border rounded-full text-sm bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <Button
                        data-testid="send-message-btn"
                        onClick={handleSend}
                        disabled={!newMessage.trim() || sending}
                        className="h-10 w-10 rounded-full bg-emerald-600 hover:bg-emerald-700 p-0"
                      >
                        <Send className="w-4 h-4 text-white" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-14 h-14 text-gray-200 dark:text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Select a conversation or start a new one</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
